import { Component, Input, OnInit, OnDestroy, Optional, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CamapignService } from '../../services/campaign.service';
import { Camapign } from '../../interfaces/campaigns.interface';

@Component({
  selector: 'app-camapign',
  standalone: false,
  templateUrl: './camapign.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./camapign.component.scss']
})
export class CamapignComponent implements OnInit, OnDestroy {
  @Input() id?: number;

  form!: FormGroup;
  loading = false;
  saving = false;
  error: string | null = null;

  dialogPartnerId?: number;

  /** Computed value shown in the readonly field */
  entryLimitPreview = '';
  transLimitPreview = '';
  private entrySub?: Subscription;

  get isNew(): boolean {
    return !this.id || this.id <= 0;
  }

  get isDialog(): boolean {
    return !!this.dialogRef;
  }

  constructor(
    private fb: FormBuilder,
    private campaignService: CamapignService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef: MatDialogRef<CamapignComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id: number; partner_id?: number }
  ) {}

  ngOnInit(): void {
    if (this.dialogData?.id) {
      this.id = this.dialogData.id;
    }
    if (this.dialogData?.partner_id) {
      this.dialogPartnerId = this.dialogData.partner_id;
    }
    if (!this.dialogData?.id) {
      const paramId = this.route.snapshot.params['id'];
      if (paramId && paramId !== 'new') {
        this.id = +paramId;
      }
    }

    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      active: [1, Validators.required],
      point_per_entry: [this.isNew ? 2 : null],
      point_per_transaction: [this.isNew ? 10 : null],
      limitation_for_transaction: [null],
      limitation_for_entry: [{ value: '', disabled: false }],
      valid: [null],
      // Helper fields — not sent to API, used only to build limitation_for_entry
      _entry_per_day:   [this.isNew ? 1  : null],
      _entry_per_week:  [this.isNew ? 7  : null],
      _entry_per_month: [this.isNew ? 30 : null],
      // Helper fields — not sent to API, used only to build limitation_for_transaction
      _trans_count:  [this.isNew ? 10   : null],
      _trans_period: ['d'],
      _trans_min:    [this.isNew ? 10   : null],
      _trans_max:    [this.isNew ? 4000 : null],
    });

    this.entrySub = this.form.get('_entry_per_day')!.valueChanges.subscribe(() => this.rebuildEntryLimit());
    this.entrySub.add(this.form.get('_entry_per_week')!.valueChanges.subscribe(() => this.rebuildEntryLimit()));
    this.entrySub.add(this.form.get('_entry_per_month')!.valueChanges.subscribe(() => this.rebuildEntryLimit()));
    this.entrySub.add(this.form.get('_trans_count')!.valueChanges.subscribe(() => this.rebuildTransLimit()));
    this.entrySub.add(this.form.get('_trans_period')!.valueChanges.subscribe(() => this.rebuildTransLimit()));
    this.entrySub.add(this.form.get('_trans_min')!.valueChanges.subscribe(() => this.rebuildTransLimit()));
    this.entrySub.add(this.form.get('_trans_max')!.valueChanges.subscribe(() => this.rebuildTransLimit()));

    // Apply defaults immediately for new campaigns (initial values don’t trigger valueChanges)
    if (this.isNew) {
      this.rebuildEntryLimit();
      this.rebuildTransLimit();
    }

    if (!this.isNew) {
      this.load();
    }
  }

  private rebuildEntryLimit(): void {
    const a = this.form.get('_entry_per_day')!.value;
    const b = this.form.get('_entry_per_week')!.value;
    const c = this.form.get('_entry_per_month')!.value;
    const parts: string[] = [];
    if (a !== null && a !== '') parts.push(`${a}:1d`);
    if (b !== null && b !== '') parts.push(`${b}:1w`);
    if (c !== null && c !== '') parts.push(`${c}:1m`);
    const value = parts.join(';');
    this.entryLimitPreview = value;
    this.form.get('limitation_for_entry')!.setValue(value, { emitEvent: false });
  }

  private parseEntryLimit(raw: string | null): void {
    if (!raw) return;
    const map: Record<string, string> = {};
    raw.split(';').forEach(part => {
      const [val, key] = part.split(':');
      if (key && val !== undefined) map[key] = val;
    });
    this.form.patchValue({
      _entry_per_day:   map['1d'] ?? null,
      _entry_per_week:  map['1w'] ?? null,
      _entry_per_month: map['1m'] ?? null,
    }, { emitEvent: false });
    this.entryLimitPreview = raw;
  }

  private rebuildTransLimit(): void {
    const count  = this.form.get('_trans_count')!.value;
    const period = this.form.get('_trans_period')!.value || 'd';
    const min    = this.form.get('_trans_min')!.value;
    const max    = this.form.get('_trans_max')!.value;
    const parts: string[] = [];
    if (count !== null && count !== '') parts.push(`${count}:1${period}`);
    parts.push(min !== null && min !== '' ? String(min) : '');
    parts.push(max !== null && max !== '' ? String(max) : '');
    // Trim trailing empty segments
    while (parts.length && parts[parts.length - 1] === '') parts.pop();
    const value = parts.join(';');
    this.transLimitPreview = value;
    this.form.get('limitation_for_transaction')!.setValue(value, { emitEvent: false });
  }

  private parseTransLimit(raw: string | null): void {
    if (!raw) return;
    const parts = raw.split(';');
    let count: string | null = null;
    let period = 'd';
    let min: string | null = null;
    let max: string | null = null;
    if (parts[0]) {
      const colonIdx = parts[0].indexOf(':');
      if (colonIdx > -1) {
        count = parts[0].substring(0, colonIdx);
        const periodPart = parts[0].substring(colonIdx + 1); // e.g. '1d'
        period = periodPart.replace(/^1/, '') || 'd';
      } else {
        count = parts[0];
      }
    }
    if (parts.length > 1 && parts[1] !== '') min = parts[1];
    if (parts.length > 2 && parts[2] !== '') max = parts[2];
    this.form.patchValue({
      _trans_count:  count,
      _trans_period: period,
      _trans_min:    min,
      _trans_max:    max,
    }, { emitEvent: false });
    this.transLimitPreview = raw;
  }

  load(): void {
    this.loading = true;
    this.campaignService.getCamapignById(this.id!).subscribe({
      next: (c: Camapign) => {
        this.form.patchValue(c);
        this.parseEntryLimit(c.limitation_for_entry ?? null);
        this.parseTransLimit(c.limitation_for_transaction ?? null);
        this.loading = false;
      },
      error: () => {
        this.error = 'Nie udało się załadować kampanii.';
        this.loading = false;
      }
    });
  }

  compareActive(a: any, b: any): boolean {
    return +a === +b;
  }

  ngOnDestroy(): void {
    this.entrySub?.unsubscribe();
  }

  cancel(): void {
    if (this.isDialog) {
      this.dialogRef.close();
    } else {
      this.router.navigate(['/campaigns']);
    }
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    // Exclude helper fields from payload
    const { _entry_per_day, _entry_per_week, _entry_per_month,
            _trans_count, _trans_period, _trans_min, _trans_max,
            ...data } = this.form.getRawValue();

    const payload = this.isNew && this.dialogPartnerId
      ? { ...data, partner_id: this.dialogPartnerId }
      : data;
    const request$ = this.isNew
      ? this.campaignService.createCamapign(payload)
      : this.campaignService.updateCamapign(this.id!, payload);

    request$.subscribe({
      next: () => {
        this.snackBar.open(
          this.isNew ? 'Kampania została utworzona.' : 'Kampania została zaktualizowana.',
          'OK',
          { duration: 3000, panelClass: 'success-snackbar' }
        );
        this.saving = false;
        if (this.isDialog) {
          this.dialogRef.close(true);
        } else {
          this.router.navigate(['/campaigns']);
        }
      },
      error: () => {
        this.snackBar.open('Wystąpił błąd podczas zapisywania.', 'OK', {
          duration: 4000,
          panelClass: 'error-snackbar'
        });
        this.saving = false;
      }
    });
  }
}
